import React, { useState, memo } from 'react';
import { BookOpen, Download, Share2, Calendar, Eye, ArrowLeft, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { Breadcrumbs } from './Breadcrumbs';
type Storybook = {
  id: number;
  title: string;
  date: string;
  coverImage: string;
  summary: string;
  storyTitle: string;
  pages: number;
  views?: number;
  pageContent: Array<{
    pageNumber: number;
    image: string;
    text: string;
  }>;
};
// Storybook data matching CreationsPage
const storybooksData: Storybook[] = [{
  id: 1,
  title: 'How Mom and Dad Met',
  date: 'Dec 18, 2024',
  coverImage: 'https://images.unsplash.com/photo-1578632767115-351597cf2477?w=800&h=600&fit=crop',
  summary: 'A heartwarming love story about two college students who met at a dance in 1985. From their first awkward conversation to their first date at the local diner, this is the story of how true love begins.',
  storyTitle: 'How Mom and Dad Met',
  pages: 12,
  views: 45,
  pageContent: [{
    pageNumber: 1,
    image: 'https://images.unsplash.com/photo-1578632767115-351597cf2477?w=1200&h=800&fit=crop',
    text: 'It was a crisp autumn evening in 1985 when Sarah walked into the college gymnasium, transformed for the night into a dance hall. The DJ was playing "Take On Me" and the room was alive with energy.'
  }, {
    pageNumber: 2,
    image: 'https://images.unsplash.com/photo-1618336753974-aae8e04506aa?w=1200&h=800&fit=crop',
    text: 'Across the room, Tom spotted her. She was wearing a blue dress and laughing with her friends. He told himself he would ask her to dance, but his feet felt frozen to the floor.'
  }, {
    pageNumber: 3,
    image: 'https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=1200&h=800&fit=crop',
    text: 'Finally, as "Careless Whisper" began to play, Tom found his courage. He walked across the dance floor, his heart pounding, and asked, "Would you like to dance?"'
  }, {
    pageNumber: 4,
    image: 'https://images.unsplash.com/photo-1513569771920-c9e1d31714af?w=1200&h=800&fit=crop',
    text: 'Sarah smiled and said yes. As they danced, they talked about everything - their classes, their dreams, their favorite movies. Time seemed to stand still.'
  }]
}, {
  id: 2,
  title: "Grandma's Apple Pie",
  date: 'Dec 15, 2024',
  coverImage: 'https://images.unsplash.com/photo-1618336753974-aae8e04506aa?w=800&h=600&fit=crop',
  summary: "A treasured family recipe passed down through four generations. Learn the secret ingredient that makes Grandma Rose's apple pie the best in town, along with memories of baking together.",
  storyTitle: "Grandma's Apple Pie Recipe",
  pages: 8,
  views: 32,
  pageContent: [{
    pageNumber: 1,
    image: 'https://images.unsplash.com/photo-1618336753974-aae8e04506aa?w=1200&h=800&fit=crop',
    text: 'Grandma Rose stood in her kitchen, the same kitchen where she had baked thousands of pies over the years. Today, she would share her secret with her granddaughter.'
  }, {
    pageNumber: 2,
    image: 'https://images.unsplash.com/photo-1578632767115-351597cf2477?w=1200&h=800&fit=crop',
    text: '"The secret," Grandma said with a twinkle in her eye, "is love. But also, a tablespoon of apple cider vinegar in the filling. It brings out the sweetness of the apples."'
  }]
}, {
  id: 3,
  title: 'Yellowstone Adventure',
  date: 'Dec 10, 2024',
  coverImage: 'https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=800&h=600&fit=crop',
  summary: "An unforgettable family road trip through Yellowstone National Park. Witness the majesty of Old Faithful, encounter wild bears, and experience the beauty of America's first national park.",
  storyTitle: 'Road Trip to Yellowstone',
  pages: 16,
  views: 28,
  pageContent: [{
    pageNumber: 1,
    image: 'https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=1200&h=800&fit=crop',
    text: 'The Rodriguez family packed their minivan before dawn. The kids had been counting down the days to this trip for months. Today, they would finally see Yellowstone.'
  }, {
    pageNumber: 2,
    image: 'https://images.unsplash.com/photo-1513569771920-c9e1d31714af?w=1200&h=800&fit=crop',
    text: 'As they drove through the park gates, everyone fell silent. The landscape was unlike anything they had ever seen - steaming geysers, colorful hot springs, and mountains that touched the sky.'
  }]
}, {
  id: 4,
  title: 'Summers at Lake Tahoe',
  date: 'Dec 5, 2024',
  coverImage: 'https://images.unsplash.com/photo-1513569771920-c9e1d31714af?w=800&h=600&fit=crop',
  summary: 'Childhood memories of magical summers spent at the family lake house. Fishing with Grandpa at dawn, swimming until sunset, and gathering around the campfire for stories under the stars.',
  storyTitle: 'Summer at Lake Tahoe',
  pages: 10,
  views: 51,
  pageContent: [{
    pageNumber: 1,
    image: 'https://images.unsplash.com/photo-1513569771920-c9e1d31714af?w=1200&h=800&fit=crop',
    text: "Every summer, the old lake house came alive with the sounds of children laughing and water splashing. For three generations, this had been the family's special place."
  }, {
    pageNumber: 2,
    image: 'https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=1200&h=800&fit=crop',
    text: 'Grandpa would wake James before sunrise. Together, they would walk down to the dock, fishing rods in hand, and watch the mist rise off the water as the sun came up.'
  }]
}, {
  id: 5,
  title: 'Max Joins the Family',
  date: 'Nov 28, 2024',
  coverImage: 'https://images.unsplash.com/photo-1614732414444-096e5f1122d5?w=800&h=600&fit=crop',
  summary: 'The heartwarming tale of how a golden retriever named Max came into our lives. From his first day at the shelter to becoming the most beloved member of the family.',
  storyTitle: 'The Day We Adopted Max',
  pages: 8,
  views: 38,
  pageContent: [{
    pageNumber: 1,
    image: 'https://images.unsplash.com/photo-1614732414444-096e5f1122d5?w=1200&h=800&fit=crop',
    text: 'The Thompson family walked into the animal shelter "just to look." But when they saw Max sitting quietly in his kennel, something magical happened.'
  }, {
    pageNumber: 2,
    image: 'https://images.unsplash.com/photo-1618336753974-aae8e04506aa?w=1200&h=800&fit=crop',
    text: 'Max looked up with his big brown eyes, and his tail started wagging slowly. Lisa knelt down, and Max gently pressed his nose against the kennel door. "This is our dog," she whispered.'
  }]
}, {
  id: 6,
  title: "Dad's Navy Years",
  date: 'Nov 22, 2024',
  coverImage: 'https://images.unsplash.com/photo-1578632767115-351597cf2477?w=800&h=600&fit=crop',
  summary: 'Stories from the high seas during the 1970s. Experience life aboard a Navy ship, visits to exotic ports around the world, and the brotherhood formed between sailors.',
  storyTitle: "Dad's Navy Stories",
  pages: 20,
  views: 19,
  pageContent: [{
    pageNumber: 1,
    image: 'https://images.unsplash.com/photo-1578632767115-351597cf2477?w=1200&h=800&fit=crop',
    text: 'In 1972, Robert Williams enlisted in the United States Navy. At eighteen years old, he had never been more than fifty miles from his hometown. The world was about to open up before him.'
  }, {
    pageNumber: 2,
    image: 'https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=1200&h=800&fit=crop',
    text: 'Boot camp was the hardest thing he had ever done. But it forged friendships that would last a lifetime. His bunkmate, Jimmy from Texas, would become like a brother to him.'
  }]
}, {
  id: 7,
  title: 'Our Christmas Traditions',
  date: 'Nov 18, 2024',
  coverImage: 'https://images.unsplash.com/photo-1618336753974-aae8e04506aa?w=800&h=600&fit=crop',
  summary: 'A celebration of family Christmas traditions passed through generations. From decorating the tree together to baking sugar cookies and attending midnight mass on Christmas Eve.',
  storyTitle: 'Christmas Traditions',
  pages: 12,
  views: 67,
  pageContent: [{
    pageNumber: 1,
    image: 'https://images.unsplash.com/photo-1618336753974-aae8e04506aa?w=1200&h=800&fit=crop',
    text: "In the Garcia household, Christmas wasn't just a day - it was a season of traditions, each one passed down with love and care through the generations."
  }, {
    pageNumber: 2,
    image: 'https://images.unsplash.com/photo-1513569771920-c9e1d31714af?w=1200&h=800&fit=crop',
    text: "Every Christmas Eve morning, Maria would wake to the smell of her mother's sugar cookies baking. The recipe came from her great-grandmother in Mexico, written on a yellowed piece of paper."
  }]
}];
type StorybookDetailPageProps = {
  storybookId: number;
  onBack: () => void;
  onReadStorybook: () => void;
};
export function StorybookDetailPage({
  storybookId,
  onBack,
  onReadStorybook
}: StorybookDetailPageProps) {
  const [currentPage, setCurrentPage] = useState(0);
  // Find the storybook by ID
  const storybook = storybooksData.find(s => s.id === storybookId) || storybooksData[0];
  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };
  const handleNextPage = () => {
    if (currentPage < storybook.pageContent.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };
  const currentPageData = storybook.pageContent[currentPage];
  return <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen" data-id="element-2264">
      <div className="px-8 py-6" data-id="element-2265">
        {/* Breadcrumbs */}
        <Breadcrumbs items={[{
        label: 'Creations',
        onClick: onBack
      }, {
        label: storybook.title
      }]} data-id="element-2266" />

        {/* Hero Section */}
        <div className="grid lg:grid-cols-3 gap-8 mb-8" data-id="element-2267">
          {/* Left: Cover Image */}
          <div className="lg:col-span-2" data-id="element-2268">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl" data-id="element-2269">
              <img src={storybook.coverImage} alt={storybook.title} className="w-full h-full object-cover" data-id="element-2270" />
            </div>
          </div>

          {/* Right: Book Info Card */}
          <div className="bg-white rounded-2xl p-6 shadow-sm h-fit" data-id="element-2271">
            <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2272">
              {storybook.title}
            </h1>

            <div className="space-y-3 mb-6" data-id="element-2273">
              <div className="flex items-center gap-3 text-gray-600" data-id="element-2274">
                <Calendar className="w-5 h-5" data-id="element-2275" />
                <span data-id="element-2276">{storybook.date}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-600" data-id="element-2277">
                <BookOpen className="w-5 h-5" data-id="element-2278" />
                <span data-id="element-2279">{storybook.pages} pages</span>
              </div>
              {storybook.views && <div className="flex items-center gap-3 text-gray-600" data-id="element-2280">
                  <Eye className="w-5 h-5" data-id="element-2281" />
                  <span data-id="element-2282">{storybook.views} views</span>
                </div>}
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6" data-id="element-2283">
              <p className="text-sm text-gray-700" data-id="element-2284">
                Created from:{' '}
                <span className="font-semibold" data-id="element-2285">{storybook.storyTitle}</span>
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3" data-id="element-2286">
              <button onClick={onReadStorybook} className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg" data-id="element-2287">
                <BookOpen className="w-5 h-5" data-id="element-2288" />
                Read Storybook
              </button>

              <div className="grid grid-cols-2 gap-3" data-id="element-2289">
                <button className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors" data-id="element-2290">
                  <Download className="w-4 h-4" data-id="element-2291" />
                  Download
                </button>
                <button className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors" data-id="element-2292">
                  <Share2 className="w-4 h-4" data-id="element-2293" />
                  Share
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Section */}
        <div className="bg-white rounded-2xl p-8 shadow-sm mb-8" data-id="element-2294">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-2295">
            About This Storybook
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-2296">
            {storybook.summary}
          </p>
        </div>

        {/* Page Preview Section */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-id="element-2297">
          <div className="p-8 border-b border-gray-200" data-id="element-2298">
            <div className="flex items-center justify-between" data-id="element-2299">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-2300">Page Preview</h2>
              <div className="flex items-center gap-2" data-id="element-2301">
                <button onClick={handlePrevPage} disabled={currentPage === 0} className={`p-2 rounded-lg transition-colors ${currentPage === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-2302">
                  <ChevronLeft className="w-6 h-6" data-id="element-2303" />
                </button>
                <span className="text-sm text-gray-600 min-w-[80px] text-center" data-id="element-2304">
                  Page {currentPage + 1} of {storybook.pageContent.length}
                </span>
                <button onClick={handleNextPage} disabled={currentPage === storybook.pageContent.length - 1} className={`p-2 rounded-lg transition-colors ${currentPage === storybook.pageContent.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-2305">
                  <ChevronRight className="w-6 h-6" data-id="element-2306" />
                </button>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2" data-id="element-2307">
            {/* Image */}
            <div className="aspect-[4/3]" data-id="element-2308">
              <img src={currentPageData.image} alt={`Page ${currentPageData.pageNumber}`} className="w-full h-full object-cover" data-id="element-2309" />
            </div>

            {/* Text */}
            <div className="p-8 flex flex-col justify-center bg-gradient-to-br from-purple-50 to-pink-50" data-id="element-2310">
              <div className="text-sm font-semibold text-purple-600 mb-4" data-id="element-2311">
                PAGE {currentPageData.pageNumber}
              </div>
              <p className="text-lg text-gray-900 leading-relaxed" data-id="element-2312">
                {currentPageData.text}
              </p>
            </div>
          </div>
        </div>

        {/* All Pages Grid */}
        <div className="mt-8" data-id="element-2313">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2314">All Pages</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2315">
            {storybook.pageContent.map((page, index) => <button key={page.pageNumber} onClick={() => setCurrentPage(index)} className={`aspect-[3/4] rounded-xl overflow-hidden transition-all ${index === currentPage ? 'ring-4 ring-purple-500 shadow-xl' : 'hover:shadow-lg'}`} data-id="element-2316">
                <img src={page.image} alt={`Page ${page.pageNumber}`} className="w-full h-full object-cover" data-id="element-2317" />
                <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/75 text-white text-xs font-medium rounded backdrop-blur-sm" data-id="element-2318">
                  {page.pageNumber}
                </div>
              </button>)}
          </div>
        </div>
      </div>
    </div>;
}